import type React from "react";

import { useIsFetching } from "@tanstack/react-query";
import { useCurrentStateAndParams } from "@uirouter/react";

import { Box } from "@similarweb/ui-components/foundations";

import { aiAgentQueryKeys } from "app/pages/sales-intelligence/modules/ai-agent/queries/queryKeyFactory";
import AIAgentTrackingService from "app/pages/sales-intelligence/modules/ai-agent/services/tracking/trackingService";
import type { Competitor } from "app/pages/sales-intelligence/modules/ai-agent/types/meeting";
import { CompetitorItem } from "app/pages/sales-intelligence/pages/ai-agent/components/CompetitorItem/CompetitorItem";
import {
    CompetitorsAutocomplete,
    MAX_COMPETITORS,
} from "app/pages/sales-intelligence/pages/ai-agent/components/CompetitorsAutocomplete/CompetitorsAutocomplete";

import { CompetitorsLoader } from "./CompetitorsLoader";
import { StyledCompetitorsContainer } from "./styles";

type CoompetitorsProps = {
    value: Competitor[];
    onChange: (value: Competitor[]) => void;
    disabled: boolean;
    defaultCompetitors: Competitor[];
};

export const MeetingCompetitors: React.FC<CoompetitorsProps> = ({ value, onChange, disabled, defaultCompetitors }) => {
    const {
        params: { domain },
    } = useCurrentStateAndParams<{ domain: string }>();

    const isLoadingCompetitors = useIsFetching({ queryKey: aiAgentQueryKeys.competitors(domain) });

    const handleAddCompetitor = (competitor: Competitor) => {
        onChange(Array.from(new Set([...(value ?? []), competitor])));
        AIAgentTrackingService.meeting.trackAddCompetitor(competitor.domain);
    };

    const handleRemoveCompetitor = (competitorDomain: string) => {
        const newValue = value?.filter(({ domain: existingDomain }) => existingDomain !== competitorDomain);
        AIAgentTrackingService.meeting.trackRemoveCompetitor(competitorDomain);
        onChange(newValue);
    };

    if (isLoadingCompetitors) {
        return <CompetitorsLoader />;
    }

    return (
        <StyledCompetitorsContainer disabled={disabled}>
            {value?.map((competitor) => (
                <CompetitorItem
                    competitor={competitor}
                    key={competitor.domain}
                    onRemoveCompetitor={handleRemoveCompetitor}
                    disabled={disabled}
                />
            ))}
            {value.length < MAX_COMPETITORS ? (
                <>
                    <Box margin="4px 0 0" />
                    <CompetitorsAutocomplete
                        value={value}
                        onAddCompetitor={handleAddCompetitor}
                        disabled={disabled}
                        defaultCompetitors={defaultCompetitors}
                    />
                </>
            ) : null}
        </StyledCompetitorsContainer>
    );
};
